import {
  DataBlockTypeDto,
  DataBlockTypeListDto,
  DataBlockVariant,
  UIConfigDto,
  UIConfigType,
  useEnabledDataBlockTypes,
} from '@lmt/shared-types';
import {
  NS_DataBlockFields,
  useScopedTranslation,
} from '@lmt/unicorn-shared/app/helpers/localization/useScopedTranslation';
import { useMemo } from 'react';
import { UIEditorData } from '../editor/UIEditorTypes';

export const dataBlockVariants = [
  DataBlockVariant.Case,
  DataBlockVariant.Visit,
  DataBlockVariant.DeviceMeasurement,
  DataBlockVariant.Measurement,
  DataBlockVariant.Patient,
];

export const dataBlockVariantsForDataBlockFieldsPage = [
  DataBlockVariant.Case,
  DataBlockVariant.Visit,
  DataBlockVariant.DeviceMeasurement,
  DataBlockVariant.Measurement,
  DataBlockVariant.SharedFields,
];

const dataBlockVariantNames: Record<DataBlockVariant, string> = {
  [DataBlockVariant.Case]: 'case',
  [DataBlockVariant.Visit]: 'visit',
  [DataBlockVariant.DeviceMeasurement]: 'device_measurement',
  [DataBlockVariant.Measurement]: 'measurement',
  [DataBlockVariant.Patient]: 'patient',
  [DataBlockVariant.SharedFields]: '',
};

export function getDataBlockTypeNameForLocalization(
  dataBlockType?: Pick<DataBlockTypeDto, 'value' | 'variant'>,
) {
  if (!dataBlockType) return '-';
  const variantName = getVariantNameById(dataBlockType.variant);
  return (
    (variantName ? variantName + '.' : '') + dataBlockType.value?.toLowerCase()
  );
}

function getVariantNameById(variant: DataBlockVariant) {
  return dataBlockVariantNames[variant];
}

export function useDataBlockTypeTranslation() {
  const i18nDataBlockTypes = useScopedTranslation(
    'data_block_types',
    NS_DataBlockFields,
  );
  return (type: DataBlockTypeDto | DataBlockTypeListDto) => {
    if (!type) return '-';

    if (type.label) {
      return type.label;
    }
    if (type.variant === DataBlockVariant.SharedFields) {
      return i18nDataBlockTypes.t(
        ('shared.' + type.value.toLowerCase()) as any,
        {
          defaultValue: type.value,
        },
      );
    }
    const typeName = getDataBlockTypeNameForLocalization(type);
    return i18nDataBlockTypes.t(typeName as any, {
      defaultValue: type.value,
    });
  };
}

export const uiConfigTypes = [
  UIConfigType.View,
  UIConfigType.Details,
  UIConfigType.Statistic,
];

export const caseInformationDataBlockTypeName = 'Case information';
export const visitInformationDataBlockTypeName = 'Visit information';
export const patientInformationDataBlockTypeName = 'Patient information';
export const sharedTypeNames = {
  patient: 'Patient',
  case: 'Case',
  visit: 'Visit',
  file: 'File',
};

export function getUIConfig(dto: UIConfigDto, type: UIConfigType) {
  switch (type) {
    case UIConfigType.View:
      return dto.view;
    case UIConfigType.Details:
      return dto.details;
    case UIConfigType.Statistic:
      return dto.statistic;
    default:
      throw new Error('Unknown UI Config type');
  }
}

export function checkUIConfigContainsField(
  config: string | null,
  fieldName: string,
) {
  if (!config) return false;
  return config.includes(`"dataKey": "${fieldName}"`);
}

export interface DataBlockValues {
  fieldValues: UIEditorData;
  sharedValues: UIEditorData;
}

export function lowerCaseFirstLetter(text: string) {
  return text[0].toLowerCase() + text.substring(1);
}
export function upperCaseFirstLetter(text: string) {
  return text[0].toUpperCase() + text.substring(1);
}
